<script setup lang="ts">
import { computed } from "vue";
import MarkdownIt from "markdown-it";

const props = defineProps<{
  content?: string | null;
  emptyText?: string;
}>();

const markdown = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true,
  typographer: false,
});

const defaultLinkOpen = markdown.renderer.rules.link_open;
markdown.renderer.rules.link_open = (tokens, index, options, environment, renderer) => {
  tokens[index].attrSet("target", "_blank");
  tokens[index].attrSet("rel", "noopener noreferrer");
  return defaultLinkOpen
    ? defaultLinkOpen(tokens, index, options, environment, renderer)
    : renderer.renderToken(tokens, index, options);
};

markdown.renderer.rules.table_open = () => '<div class="markdown-table-scroll"><table>\n';
markdown.renderer.rules.table_close = () => "</table></div>\n";

const rendered = computed(() =>
  markdown.render(String(props.content || props.emptyText || "")),
);
</script>

<template>
  <div class="markdown-content" v-html="rendered" />
</template>

<style scoped>
.markdown-content{min-width:0;color:inherit;line-height:1.72;overflow-wrap:anywhere}
.markdown-content :deep(> :first-child){margin-top:0}
.markdown-content :deep(> :last-child){margin-bottom:0}
.markdown-content :deep(h1),.markdown-content :deep(h2),.markdown-content :deep(h3),.markdown-content :deep(h4){margin:1.25em 0 .55em;color:var(--ink);font-size:1em;letter-spacing:0}
.markdown-content :deep(p){margin:.6em 0;white-space:normal}
.markdown-content :deep(ul),.markdown-content :deep(ol){margin:.6em 0;padding-left:1.4em}
.markdown-content :deep(blockquote){margin:.8em 0;padding:8px 12px;border-left:3px solid var(--brand);background:#f5f8f8;color:var(--muted)}
.markdown-content :deep(pre){max-width:100%;padding:12px;overflow:auto;background:#f3f5f5}
.markdown-content :deep(code){overflow-wrap:anywhere}
.markdown-content :deep(.markdown-table-scroll){width:100%;max-width:100%;margin:12px 0;overflow-x:auto;overscroll-behavior-x:contain;touch-action:pan-x pan-y;-webkit-overflow-scrolling:touch}
.markdown-content :deep(table){width:max-content;min-width:100%;border-collapse:collapse}
.markdown-content :deep(th),.markdown-content :deep(td){min-width:110px;padding:9px 10px;border:1px solid var(--line);vertical-align:top;text-align:left;white-space:normal}
.markdown-content :deep(th){position:sticky;top:0;background:#eef3f3;color:var(--ink);font-size:12px}
.markdown-content :deep(td){background:#fff;font-size:13px}
.markdown-content :deep(a){color:var(--brand-dark)}
@media(max-width:700px){.markdown-content{font-size:14px;line-height:1.65}.markdown-content :deep(.markdown-table-scroll){margin:10px 0}.markdown-content :deep(th),.markdown-content :deep(td){min-width:120px;padding:8px;font-size:12px}}
</style>
